/* ---------------------------------------------------------------------------
   Everything that changes the state, in one place.

   The screens call these and nothing else; no screen writes to the store
   directly. That is worth the indirection here for one reason: a rating form has
   several ways to fill the same field — typed, filled from the register, filled
   from the era table — and each has to record a different provenance. Keeping
   them together is what stops a fourth one being added that records none.
   --------------------------------------------------------------------------- */

import { eraRowFor, type VehicleId } from '../cbplr/index.ts';
import type { Bridge } from '../data/types.ts';
import { active, buildTenant, byId, requestedTenant, resolveTenant, setActive } from '../tenant/registry.ts';
/* TEMPORARY — demonstration data for FWO. Delete with src/data/fwo-demo.ts. */
import { demoDefectsFor, demoRatingsFor, withDemoBridges } from '../data/fwo-demo.ts';
import * as api from './api.ts';
import { eraFill, prefillFrom, readNumber } from './input.ts';
import { standardDefaults } from './defaults.ts';
import { NO_LOSS, assumedDamage, damageFromInspection, damageKeys } from './damage.ts';
import type { RegisterDefect } from '../data/types.ts';
import {
  CONVENTIONAL,
  emptyDraft,
  fieldsFor,
  store,
  type GirderPosition,
  type RatingDraft,
  type RatingFamily,
  type Screen,
  type SourceTier,
  type State,
} from './store.ts';

/**
 * TEMPORARY — the FWO demonstration; delete with src/data/fwo-demo.ts. Its
 * register and its one rating, ADDED to what the deployment answered, the way
 * `hydrate` in src/core/store.ts adds the register: a real row wins on id.
 */
function withDemo(
  bridges: Bridge[],
  ratings: api.RatingRecord[],
  defects: RegisterDefect[] | null,
): { bridges: Bridge[]; ratings: api.RatingRecord[]; defects: RegisterDefect[] | null } {
  const tenant = active().id;
  const haveR = new Set(ratings.map((r) => r.id));
  const haveD = new Set((defects ?? []).map((d) => d.id));
  const demoDefects = demoDefectsFor(tenant).filter((d) => !haveD.has(d.id));
  return {
    bridges: withDemoBridges(tenant, bridges),
    ratings: [...ratings, ...demoRatingsFor(tenant).filter((r) => !haveR.has(r.id))],
    defects: defects === null && demoDefects.length === 0 ? null : [...(defects ?? []), ...demoDefects],
  };
}

function bridgeById(s: Readonly<State>, id: string) {
  return s.bridges?.find((b) => b.id === id) ?? null;
}

/** Tiers an automatic fill may replace: nobody chose these values for this bridge. */
const ASSUMED: ReadonlySet<SourceTier | undefined> = new Set([undefined, 'standard', 'era']);

function isBlank(draft: RatingDraft, key: string): boolean {
  return (draft.fields[key] ?? '').trim() === '';
}

/** The span a draft names, or null where it names none. */
function spanOf(draft: RatingDraft): number | null {
  const n = Number(draft.spanIndex.trim());
  return draft.spanIndex.trim() !== '' && Number.isInteger(n) && n >= 1 ? n : null;
}

/**
 * The draft with every blank or assumed field this family asks for filled, as
 * far as the register's year and the rating standard can take it.
 *
 * THE ERA ROW FIRST, for the RC families, where the year is known — that gives
 * the f′c and fy the allowables are keyed to. Then the standard's own values:
 * the allowables for whatever grade the form now holds, the damage inputs at
 * zero, CBPLR's constants and the workbook loads. A value somebody typed,
 * measured or took off a drawing is never touched; an `era` or `standard` one
 * is recomputed, so a changed grade carries its allowables with it.
 */
function withAssumptions(s: Readonly<State>, draft: RatingDraft): RatingDraft {
  const fields = { ...draft.fields };
  const sources = { ...draft.sources };
  const damageBasis = { ...draft.damageBasis };
  const next = { ...draft, fields, sources, damageBasis };
  const replaceable = (key: string) => isBlank(next, key) || ASSUMED.has(sources[key]);

  /* THE DAMAGE INPUTS FROM THE INSPECTION (LC-20), before the standard's zeros:
     the open defects on this span and girder, against the latest accepted
     inspection. A value the inspection gave is re-read when the span, girder or
     family changes, and cleared where it no longer applies; one somebody typed
     or recorded as *no loss found* is left alone. */
  const bridge = draft.bridgeId === null ? null : bridgeById(s, draft.bridgeId);
  if (bridge !== null) {
    const fill = damageFromInspection(bridge, s.defects ?? [], {
      family: draft.family,
      position: draft.position,
      spanIndex: spanOf(draft),
      girderCount: readNumber(fields['girderCount']),
    });
    next.inspectionId = fill.inspectionId;
    for (const key of damageKeys(draft.family)) {
      const fromDefect = sources[key] === 'inspection' && damageBasis[key] !== NO_LOSS;
      const value = fill.fields[key];
      if (value !== undefined && (fromDefect || replaceable(key))) {
        fields[key] = String(value);
        sources[key] = 'inspection';
        damageBasis[key] = fill.basis[key]!;
      } else if (fromDefect) {
        delete fields[key];
        delete sources[key];
        delete damageBasis[key];
      }
    }
  }

  if (draft.family !== 'PCDG') {
    const year = draft.bridgeId === null ? null : (bridgeById(s, draft.bridgeId)?.year ?? null);
    const era = eraFill(
      year,
      eraRowFor(s.profile.eras.concrete, year),
      eraRowFor(s.profile.eras.rebar, year),
    );
    /* A grade that did not come from the era table — the model's, or typed —
       keeps its own allowables rather than getting the era's. */
    const eraGrade = (key: string) => replaceable(key.startsWith('concrete') ? 'concrete.strength' : 'rebar.strength');
    for (const [key, value] of Object.entries(era.fields)) {
      if (!replaceable(key) || !eraGrade(key)) continue;
      fields[key] = value;
      sources[key] = 'era';
    }
  }

  const known: Record<string, number | undefined> = {};
  for (const [key, raw] of Object.entries(fields)) {
    const n = readNumber(raw);
    if (n === null) continue;
    /* An assumed allowable is recomputed from the grade, so it is not "known" —
       a standard one always, and an era one once its grade is no longer the
       era's. */
    if (key.includes('.allowable.')) {
      const grade = key.startsWith('concrete') ? 'concrete.strength' : 'rebar.strength';
      if (sources[key] === 'standard') continue;
      if (sources[key] === 'era' && sources[grade] !== 'era') continue;
    }
    known[key] = n;
  }
  const wanted = new Set(fieldsFor(draft.family, draft.position).map((f) => f.key));
  for (const d of standardDefaults(draft.family, known)) {
    if (!wanted.has(d.key)) continue;
    if (!replaceable(d.key)) continue;
    fields[d.key] = String(d.value);
    sources[d.key] = 'standard';
  }
  return next;
}

/** The basis of every damage input marked `inspection`, for the save. */
function damageOf(draft: RatingDraft): { damage?: Record<string, string> } {
  const damage: Record<string, string> = {};
  for (const [key, basis] of Object.entries(draft.damageBasis)) {
    if (draft.sources[key] === 'inspection') damage[key] = basis;
  }
  return Object.keys(damage).length === 0 ? {} : { damage };
}

/** Told when a rating is accepted here, with its structure — see onRatingAccepted in ./mount.ts. */
export const acceptedWatchers = new Set<(bridgeId: string) => void>();

export const actions = {
  go(screen: Screen): void {
    store.set({ screen, error: null });
  },

  setSearch(search: string): void {
    store.set({ search });
  },

  select(id: string | null): void {
    /* A rating opened on another structure is not this one's. One not read
       yet is kept: an address opened cold names it before the ratings arrive. */
    const opened = store.get().opened;
    const of = (store.get().ratings ?? []).find((r) => r.id === opened)?.bridgeId;
    store.set({ selected: id, ...(of !== undefined && of !== id ? { opened: null } : {}) });
  },

  /** Open one rating on record on the Ratings screen, or null for the one the structure stands on. */
  openRecord(id: string | null): void {
    const r = id === null ? null : (store.get().ratings ?? []).find((x) => x.id === id);
    store.set({ opened: id, screen: 'ratings', error: null, ...(r ? { selected: r.bridgeId } : {}) });
  },

  setChartVehicle(vehicle: VehicleId): void {
    store.set({ chartVehicle: vehicle });
  },

  /* --------------------------- the draft --------------------------- */

  /**
   * Begin rating a structure.
   *
   * Fills what the register genuinely holds and marks it `drawing`; leaves
   * everything else empty. See the note on `prefillFrom` for why that is three
   * fields out of eighteen and not a gap to be papered over.
   */
  startRating(bridgeId: string): void {
    const s = store.get();
    const bridge = bridgeById(s, bridgeId);
    const draft = emptyDraft();
    draft.bridgeId = bridgeId;

    if (bridge) {
      const filled = prefillFrom(bridge);
      draft.fields = { ...filled.fields };
      draft.sources = { ...filled.sources };
    }

    store.set({
      draft: withAssumptions(s, draft),
      saved: null,
      screen: 'rate',
      error: null,
      selected: bridgeId,
      modelFill: null,
    });
    /* The structure's own model, where it has one, without being asked — it
       only replaces what was assumed above. */
    void actions.fillFromModel({ quiet: true });
  },

  /**
   * Change the family being rated.
   *
   * KEEPS THE VALUES THAT STILL MEAN THE SAME THING and drops the rest. Span,
   * slab thickness, the unit weights, the wearing course and all six material
   * values carry the same meaning in both families and are kept with their
   * provenance intact. A flat slab's `slabWidth` is not a T-beam's anything, and
   * carrying it across under a name the new form does not draw would leave a
   * value in the saved input that nothing on screen ever showed.
   */
  setFamily(family: RatingFamily): void {
    const draft = store.get().draft;
    if (draft.family === family) return;

    const shared = new Set(
      fieldsFor(family, family === 'RCFS' ? null : 'exterior').map((f) => f.key),
    );
    const fields: Record<string, string> = {};
    const sources: Record<string, SourceTier> = {};
    const damageBasis: Record<string, string> = {};
    for (const [key, value] of Object.entries(draft.fields)) {
      if (!shared.has(key)) continue;
      fields[key] = value;
      const tier = draft.sources[key];
      if (tier !== undefined) sources[key] = tier;
      const basis = draft.damageBasis[key];
      if (basis !== undefined) damageBasis[key] = basis;
    }

    store.set({
      draft: withAssumptions(store.get(), {
        ...draft,
        family,
        /* A girder family needs a position and RCFS must not have one. Defaulted
           to null rather than to 'interior': which girder is being rated is a
           decision, and the form asks for it. */
        position: null,
        fields,
        sources,
        damageBasis,
      }),
      saved: null,
      error: null,
    });
    void actions.fillFromModel({ quiet: true });
  },

  setPosition(position: GirderPosition): void {
    const s = store.get();
    store.set({ draft: withAssumptions(s, { ...s.draft, position }), saved: null });
    /* The exterior girder's cantilever and footway are the model's to answer. */
    void actions.fillFromModel({ quiet: true });
  },

  /** A field typed into. Typing always claims authorship of the value. */
  setField(key: string, value: string): void {
    const draft = store.get().draft;
    const damageBasis = { ...draft.damageBasis };
    delete damageBasis[key];
    const typed: RatingDraft = {
      ...draft,
      fields: { ...draft.fields, [key]: value },
      sources: { ...draft.sources, [key]: 'user' },
      /* A typed value is the engineer's, not a defect's. */
      damageBasis,
    };
    store.set({
      /* A grade typed in carries its allowables with it — the assumed ones only. */
      draft:
        key === 'concrete.strength' || key === 'rebar.strength'
          ? withAssumptions(store.get(), typed)
          : typed,
      /* A saved rating is about the input as it was. Editing the form after
         saving must not leave the result panel implying it still describes what
         is on screen. */
      saved: null,
    });
  },

  /** Re-attribute a value without changing it. */
  setSource(key: string, tier: SourceTier): void {
    const draft = store.get().draft;
    /* Only the inspection names a basis; picking `inspection` by hand names
       none, which acceptance then refuses (LC-21). */
    const damageBasis = { ...draft.damageBasis };
    if (tier !== 'inspection') delete damageBasis[key];
    store.set({ draft: { ...draft, sources: { ...draft.sources, [key]: tier }, damageBasis } });
  },

  /**
   * Record *inspected, no loss found* for every damage input still assumed
   * (LC-21): zero, marked `inspection`, against the latest accepted
   * inspection. Refused where there is no accepted inspection to have found it.
   */
  recordNoLoss(): void {
    const draft = store.get().draft;
    if (draft.inspectionId === null) {
      store.set({ error: 'No accepted inspection of this structure is on record, so none can be said to have found no loss.' });
      return;
    }
    const fields = { ...draft.fields };
    const sources = { ...draft.sources };
    const damageBasis = { ...draft.damageBasis };
    for (const key of assumedDamage(draft.family, draft.sources, draft.damageBasis, draft.inspectionId)) {
      fields[key] = '0';
      sources[key] = 'inspection';
      damageBasis[key] = NO_LOSS;
    }
    store.set({ draft: { ...draft, fields, sources, damageBasis }, saved: null, error: null });
  },

  setNote(note: string): void {
    store.set({ draft: { ...store.get().draft, note } });
  },

  setSpanIndex(spanIndex: string): void {
    const s = store.get();
    /* The span decides which defects are on the member (LC-20). */
    store.set({ draft: withAssumptions(s, { ...s.draft, spanIndex }) });
  },

  /**
   * Fill the conventional unit weights and strip width.
   *
   * Recorded as `user`, not as some fourth tier meaning "the application
   * suggested it". Somebody pressed the button, and 24 kN/m³ for concrete is a
   * value an engineer owns rather than a measurement.
   */
  fillConventional(): void {
    const draft = store.get().draft;
    const fields = { ...draft.fields };
    const sources = { ...draft.sources };
    for (const [key, value] of Object.entries(CONVENTIONAL)) {
      /* Does not overwrite. A field somebody has already filled is theirs. */
      if ((fields[key] ?? '').trim() !== '') continue;
      fields[key] = value;
      sources[key] = 'user';
    }
    store.set({ draft: { ...draft, fields, sources }, saved: null });
  },

  /**
   * Fill the six material values from the era table for the year built.
   *
   * Both levels of both materials, from the same row — which is the shape of
   * correction C8. Does nothing where the register has no year, because a
   * structure whose age nobody recorded is not a structure built before 1959.
   */
  fillFromEra(): void {
    const s = store.get();
    const draft = s.draft;
    const bridge = draft.bridgeId === null ? null : bridgeById(s, draft.bridgeId);
    const year = bridge?.year ?? null;
    if (year === null) {
      store.set({ error: 'The register has no year of construction for this structure, so the era table cannot be applied.' });
      return;
    }

    const filled = eraFill(
      year,
      eraRowFor(s.profile.eras.concrete, year),
      eraRowFor(s.profile.eras.rebar, year),
    );
    if (Object.keys(filled.fields).length === 0) {
      store.set({ error: `No era row covers ${year}.` });
      return;
    }

    store.set({
      draft: {
        ...draft,
        fields: { ...draft.fields, ...filled.fields },
        sources: { ...draft.sources, ...filled.sources },
      },
      saved: null,
      error: null,
    });
  },

  /**
   * Fill from the structure's own CSiBridge cloud model.
   *
   * Marked `model` on every field the model states, which is the strongest provenance
   * this application has: it is the design document, not a transcription of one.
   * Fields the model does not state are left alone rather than cleared — a
   * half-typed form is not thrown away by asking the model what it knows.
   *
   * THE NOTES ARE THE POINT, as much as the values. The model says what it
   * cannot answer and why, and those go on screen: "no materials in this
   * export", "the centroid needs the bar schedule". A fill that silently left
   * eleven fields blank would look like a failure rather than an answer.
   */
  async fillFromModel(options: { quiet?: boolean } = {}): Promise<void> {
    const quiet = options.quiet === true;
    const asked = store.get().draft;
    if (asked.bridgeId === null) {
      if (!quiet) store.set({ error: 'Choose a structure before reading its model.' });
      return;
    }

    if (!quiet) store.set({ busy: true, error: null, modelFill: null });
    try {
      const report = await api.fetchModelFill(asked.bridgeId, asked.family, asked.position);

      /* THE ANSWER IS APPLIED TO THE DRAFT AS IT IS NOW, not as it was asked
         for, and only if it is still the same question: a family or girder
         changed while the request was out would otherwise be filled with
         another form's values. */
      const s = store.get();
      const draft = s.draft;
      if (
        draft.bridgeId !== asked.bridgeId ||
        draft.family !== asked.family ||
        draft.position !== asked.position
      ) {
        if (!quiet) store.set({ busy: false });
        return;
      }
      if (!report.readable) {
        store.set(quiet ? { modelFill: report } : { busy: false, modelFill: report, error: report.reason ?? null });
        return;
      }

      const fields = { ...draft.fields };
      const sources = { ...draft.sources };
      /* A MODEL VALUE replaces what is there when asked for, and only what was
         assumed when it was not — a quiet read never undoes something typed. A
         STANDARD value only ever replaces a blank or another assumption. */
      for (const [key, value] of Object.entries(report.fields ?? {})) {
        const tier: SourceTier = report.sources?.[key] === 'standard' ? 'standard' : 'model';
        const current = (fields[key] ?? '').trim() === '' ? undefined : sources[key];
        if ((tier === 'standard' || quiet) && !ASSUMED.has(current)) continue;
        fields[key] = String(value);
        sources[key] = tier;
      }

      store.set({
        /* Re-assumed afterwards: the model's grade carries its allowables. */
        draft: withAssumptions(s, { ...draft, fields, sources }),
        modelFill: report,
        saved: null,
        ...(quiet ? {} : { busy: false, error: null }),
      });
    } catch (err) {
      /* A quiet read of a structure with no model is not news. */
      if (quiet) return;
      const status = err instanceof api.ApiError ? err.status : 0;
      store.set({
        busy: false,
        /* A 404 is the ordinary answer for a structure nobody has modelled, so
           it is reported as information rather than as a failure. */
        error:
          status === 404
            ? 'This structure has no imported model with a stored CSiBridge export.'
            : err instanceof Error
              ? err.message
              : String(err),
      });
    }
  },

  clearDraft(): void {
    store.set({ draft: emptyDraft(), saved: null, error: null, modelFill: null });
  },

  /* --------------------------- the deployment --------------------------- */
  /**
   * Adopt the department this session belongs to.
   *
   * Called twice, exactly as Bridge Watch calls its own — once at boot from
   * ./main.ts with `?tenant=` and the build's `VITE_TENANT`, and once from
   * `connect` with what `/api/health` says the signed-in user's row holds. The
   * second is the AUTHORITATIVE one on a shared deployment and it is the
   * SERVER'S answer: the client never gets to choose which register it reads,
   * only which labels it draws over it. See the header of
   * src/tenant/registry.ts.
   *
   * THIS WAS MISSING AND IT MATTERED. `/api/health` has always carried `tenant`
   * and this application read the role and the capabilities off the same report
   * and dropped it, so the letterhead came from `VITE_TENANT` alone — Nepal, on
   * any deployment serving more than one department. A rating is a document an
   * engineer signs and a posting sign is a legal instrument; the agency block on
   * it cannot be the wrong government's.
   *
   * A CHANGE DROPS THE ROWS IN HAND, for the same reason Bridge Watch drops
   * them: they were fetched for the department that was in force, and showing
   * them under another's branding until the next fetch lands is the exact
   * failure this arrangement exists to prevent. The draft goes with them — its
   * `bridgeId` names a structure in a register that is no longer loaded.
   */
  tenantResolved(session?: string | null): void {
    const next = resolveTenant({
      url: new URLSearchParams(window.location.search).get('tenant'),
      build: buildTenant(),
      session: session ?? null,
    });
    if (!setActive(next)) return;
    /* The family survives and nothing else does. Somebody who had chosen PCDG
       has said something about the kind of bridge they are rating, not about
       which department's register it is in — and re-picking it after a boot that
       resolved the tenant a moment late would be the application forgetting
       something it was told. */
    const family = store.get().draft.family;
    store.set({
      bridges: null,
      ratings: [],
      defects: null,
      draft: { ...emptyDraft(), family },
    });
  },


  /**
   * Ask the deployment what it is and what this caller may do.
   *
   * Every unwelcome answer is a distinct state rather than one `error`, because
   * the remedies are different and the screens say which: signed out means sign
   * in, behind means run the migration, unreachable means the network.
   */
  async connect(): Promise<void> {
    try {
      const report = await api.health();
      /* One reader for the caller, used by both branches. Reading the role and
         the capabilities out of the report by hand is exactly how this went
         wrong the first time — see the note on HealthReport in ./api.ts. */
      const caller = api.callerOf(report);
      if (api.schemaBehind(report)) {
        store.set({
          backend: 'behind',
          backendNote: report.checks['schema'] ?? null,
          identity: caller.email,
          role: caller.role,
          capabilities: caller.capabilities,
        });
        return;
      }
      /* A SESSION FROM ANOTHER COUNTRY IS NOT THIS WINDOW'S: the server would
         serve that register's structures under this one's letterhead. Signed
         out, saying so — see refreshIdentity in src/core/store.ts. */
      const wanted = requestedTenant();
      if (caller.email && wanted && report.tenant && report.tenant !== wanted) {
        const held = byId(report.tenant)?.name ?? report.tenant;
        const here = byId(wanted)?.name ?? wanted;
        store.set({
          backend: 'signed-out',
          backendNote: `This browser is signed in to ${held}, not ${here}. Sign in to work in ${here}’s register.`,
        });
        return;
      }
      /* BEFORE the register is fetched, and that ordering is the point: the
         rows have to arrive under the department they belong to, not be
         re-branded a moment after they land. */
      actions.tenantResolved(report.tenant ?? null);
      store.set({
        backend: 'live',
        backendNote: null,
        identity: caller.email,
        role: caller.role,
        capabilities: caller.capabilities,
      });
      await actions.loadRegister();
    } catch (err) {
      const status = err instanceof api.ApiError ? err.status : 0;
      store.set({
        backend: status === 401 || status === 403 ? 'signed-out' : 'unreachable',
        backendNote: err instanceof Error ? err.message : String(err),
        /* TEMPORARY — the FWO demonstration draws without a deployment, as the register does. */
        ...(demoRatingsFor(active().id).length > 0 ? withDemo([], [], null) : {}),
      });
    }
  },

  async loadRegister(): Promise<void> {
    try {
      const [bridges, ratings] = await Promise.all([api.fetchRegister(), api.fetchRatings()]);
      /* The defects are read beside them and may fail on their own: a register
         with no defect list still rates, and the damage card says it could not
         read them rather than that there are none. */
      const defects = await api.fetchDefects().catch(() => null);
      store.set({ ...withDemo(bridges, ratings, defects), error: null });
    } catch (err) {
      /* The register and the ratings are read together and reported together: a
         screen with structures and no ratings would show every bridge as never
         rated, which is a claim rather than a loading state. */
      store.set({
        bridges: null,
        ratings: null,
        defects: null,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  },

  /* --------------------------- saving --------------------------- */

  /**
   * Send the input and store what came back.
   *
   * `assembled.input` is the caller's business to have checked — the Save button
   * is not offered until the form assembles — but it is checked again here
   * rather than asserted, because a disabled button is a suggestion and this is
   * the last place before a record is written.
   */
  async save(input: unknown): Promise<void> {
    const s = store.get();
    const draft = s.draft;
    if (draft.bridgeId === null || input === null) {
      store.set({ error: 'The form is not complete enough to rate.' });
      return;
    }

    store.set({ busy: true, error: null });
    try {
      const spanIndex = Number(draft.spanIndex.trim());
      const rating = await api.saveRating({
        bridgeId: draft.bridgeId,
        family: draft.family,
        continuity: draft.continuity,
        ...(draft.position === null ? {} : { position: draft.position }),
        ...(draft.spanIndex.trim() !== '' && Number.isInteger(spanIndex) && spanIndex >= 1
          ? { spanIndex }
          : {}),
        profileId: s.profile.id,
        input,
        sources: draft.sources,
        ...(draft.note.trim() === '' ? {} : { note: draft.note.trim() }),
        /* The model the form was filled from, where it was filled from one. */
        ...(s.modelFill?.readable
          ? {
              modelId: s.modelFill.modelId,
              ...(s.modelFill.revision ? { modelRevision: s.modelFill.revision } : {}),
            }
          : {}),
        /* What the damage inputs rest on (LC-20); the deployment checks each. */
        ...(draft.inspectionId === null ? {} : { inspectionId: draft.inspectionId }),
        ...damageOf(draft),
      });
      store.set({ saved: rating, busy: false, error: null });
      await actions.loadRegister();
    } catch (err) {
      store.set({ busy: false, error: err instanceof Error ? err.message : String(err) });
    }
  },

  async moveState(id: string, state: api.RatingState): Promise<void> {
    store.set({ busy: true, error: null });
    try {
      const rating = await api.setRatingState(id, state);
      const saved = store.get().saved;
      store.set({
        busy: false,
        ...(saved?.id === id ? { saved: rating } : {}),
      });
      await actions.loadRegister();
      /* An accepted rating can restrict or post the bridge (LC-21), which the
         page's own register has to hear about; it used to read "Open" and offer
         Restrict until a reload (QA 2026-09-30). */
      if (rating.state === 'accepted') for (const fn of acceptedWatchers) fn(rating.bridgeId);
    } catch (err) {
      store.set({ busy: false, error: err instanceof Error ? err.message : String(err) });
    }
  },

  dismissError(): void {
    store.set({ error: null });
  },
};
